'use client';

import { c as _c } from "react/compiler-runtime";
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React from 'react';
import { CookiesProvider } from 'react-cookie';
import { NavermapsProvider } from 'react-naver-maps';
import { ENV, IS_DEV } from '@/constants';
import { MswProvider } from '@/mocks/MswProvider';
import { GlobalController } from '@/modules';
import { useMountDeviceInfoStore } from '@/stores/DeviceStore';
import { cookies } from '@/utils/cookies';
import { ChannelTalk } from '../ChannelTalk';
import { AuthProvider } from './_components/AuthProvider';
import { GuestProvider } from './_components/GuestProvider';
import { HistoryObserver } from './_components/HistoryObserver';
import { JoinController } from './_components/JoinController';
import { PageViewEventLogger } from './_components/PageViewEventLogger';
import { useGlobalEventListener } from './_hooks/useGlobalEventListener';
import { useGtmEventClickListener } from './_hooks/useGtmEventClickListener';
import { useLoginGtmEventListener } from './_hooks/useLoginGtmEventListener';
interface GlobalProviderProps {
  children: React.ReactNode;
}
const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onSuccess: async (_data, _variables, _context, mutation) => {
      if (mutation.meta) {
        const {
          reset,
          invalidates
        } = mutation.meta;
        if (reset) {
          queryClient.resetQueries({
            predicate: query => {
              const hasIncludeQueryKey = reset.some(queryKeys => {
                const hasAllQueryKey = queryKeys.every(queryKey => query.queryKey.includes(queryKey));
                return hasAllQueryKey;
              });
              return hasIncludeQueryKey;
            }
          });
        }
        if (invalidates) {
          await queryClient.invalidateQueries({
            predicate: query => {
              const hasIncludeQueryKey = invalidates.some(queryKeys => {
                const hasAllQueryKey = queryKeys.every(queryKey => query.queryKey.includes(queryKey));
                return hasAllQueryKey;
              });
              return hasIncludeQueryKey;
            }
          });
        }
      }
    }
  }),
  defaultOptions: {
    queries: {
      staleTime: 300_000,
      retry: 2,
      retryDelay: 1_000
    }
  }
});
export const GlobalProvider = t0 => {
  const $ = _c(11);
  const {
    children
  } = t0;
  useMountDeviceInfoStore();
  useGlobalEventListener();
  useGtmEventClickListener();
  useLoginGtmEventListener();
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      path: "/"
    };
    t2 = <PageViewEventLogger />;
    t3 = <HistoryObserver />;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <GlobalController />;
    t5 = <JoinController />;
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  let t6;
  if ($[5] !== children) {
    t6 = <AuthProvider><GuestProvider>{t4}{t5}<NavermapsProvider ncpClientId={ENV.NEXT_PUBLIC_NAVER_CLOUD_CLIENT_ID}>{children}</NavermapsProvider></GuestProvider></AuthProvider>;
    $[5] = children;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = IS_DEV ? <ReactQueryDevtools buttonPosition="bottom-left" initialIsOpen={false} /> : null;
    t8 = <ChannelTalk />;
    $[7] = t7;
    $[8] = t8;
  } else {
    t7 = $[7];
    t8 = $[8];
  }
  let t9;
  if ($[9] !== t6) {
    t9 = <MswProvider><QueryClientProvider client={queryClient}><CookiesProvider cookies={cookies} defaultSetOptions={t1}>{t2}{t3}{t6}{t7}{t8}</CookiesProvider></QueryClientProvider></MswProvider>;
    $[9] = t6;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  return t9;
};